import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "@/api/client";

/**
 * Admin users tab hooks (DUE-100, Phase F; re-keyed to `users.id` in DUE-191).
 *
 * Schemas mirror the live FastAPI shapes in
 * `src/api/schemas/admin_config.py`:
 *   - `AdminUserIdentity` — identity fields shared by every user DTO
 *   - `UserSearchRow` — `GET /api/admin/users/search`
 *   - `UserTopRow`    — `GET /api/admin/users/top`
 *   - `UserCreditsView` + `CreditTxRow` — `GET /api/admin/users/{user_id}/credits`
 *   - `PaymentRow`    — `GET /api/admin/users/{user_id}/payments`
 *
 * Rows are keyed by `users.id`, not `tg_id` — iOS guest / Apple-only
 * accounts have no `tg_id`.
 *
 * Note: the credit ledger row carries `amount` (not `delta`), plus
 * `reading_id`, `admin_user_id` and `meta` — kept faithful to the
 * Pydantic model so backend changes surface here as zod failures.
 */

const decimal = z.coerce.number();

export const PLATFORMS = [
  "telegram",
  "ios_guest",
  "apple",
  "apple_ex_guest",
  "web",
] as const;
export const UserPlatformSchema = z.enum(PLATFORMS);
export type UserPlatform = z.infer<typeof UserPlatformSchema>;

/** Filter value used by the Users tab; "all" means "no platform filter". */
export type PlatformFilter = UserPlatform | "all";

export const AdminUserIdentitySchema = z.object({
  id: z.number().int(),
  // null for iOS guest / Apple-only accounts — never used as a key or link.
  tg_id: z.number().int().nullable(),
  username: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  device_id: z.string().nullable(),
  platform: UserPlatformSchema,
});
export type AdminUserIdentity = z.infer<typeof AdminUserIdentitySchema>;

export const UserSearchRowSchema = AdminUserIdentitySchema.extend({
  locale: z.string(),
  credits: z.number().int().nonnegative(),
  created_at: z.string(),
});
export const UserSearchListSchema = z.array(UserSearchRowSchema);
export type UserSearchRow = z.infer<typeof UserSearchRowSchema>;

export const UserTopRowSchema = AdminUserIdentitySchema.extend({
  created_at: z.string(),
  metric: decimal,
});
export const UserTopListSchema = z.array(UserTopRowSchema);
export type UserTopRow = z.infer<typeof UserTopRowSchema>;

export const CreditTxRowSchema = z.object({
  id: z.number().int(),
  feature: z.string(),
  amount: z.number().int(),
  reason: z.string(),
  payment_id: z.number().int().nullable(),
  reading_id: z.number().int().nullable(),
  admin_user_id: z.number().int().nullable(),
  cost_usd: decimal.nullable(),
  meta: z.record(z.unknown()),
  created_at: z.string(),
});
export type CreditTxRow = z.infer<typeof CreditTxRowSchema>;

export const UserCreditsViewSchema = AdminUserIdentitySchema.extend({
  // DUE-180: backward-compatible default so a stale cached response (or an
  // older backend during a rolling deploy) without the field doesn't fail
  // schema validation — just reads as "not admin".
  is_admin: z.boolean().default(false),
  synastry_credits: z.number().int(),
  composite_credits: z.number().int(),
  // DUE-411: default keeps a response from an older backend parseable.
  relation_named_credits: z.number().int().default(0),
  relation_words_credits: z.number().int().default(0),
  transits_until: z.string().nullable(),
  legacy_credits: z.number().int(),
  total_llm_cost_usd: decimal,
  history: z.array(CreditTxRowSchema),
});
export type UserCreditsView = z.infer<typeof UserCreditsViewSchema>;

export const RefundResponseSchema = z.object({
  payment_id: z.number().int(),
  status: z.string(),
  charge_id: z.string().nullable(),
});
export type RefundResponse = z.infer<typeof RefundResponseSchema>;

export const PaymentRowSchema = z.object({
  id: z.number().int(),
  product_code: z.string(),
  amount_xtr: z.number().int().nullable(),
  amount_usd: decimal.nullable(),
  // DUE-492: price in the App Store receipt currency; null for other providers.
  amount_local: decimal.nullable().default(null),
  currency: z.string().nullable().default(null),
  status: z.string(),
  provider: z.string(),
  created_at: z.string(),
  refundable: z.boolean(),
});
export const PaymentListSchema = z.array(PaymentRowSchema);
export type PaymentRow = z.infer<typeof PaymentRowSchema>;

export const UserAdminSchema = z.object({
  user_id: z.number().int(),
  tg_id: z.number().int().nullable(),
  is_admin: z.boolean(),
});
export type UserAdmin = z.infer<typeof UserAdminSchema>;

/**
 * DUE-296 — regenerate a user's readings. Mirrors `RegenerateItemDTO` /
 * `RegenerateJobDTO` in `src/api/schemas/admin_config.py`; `day` is an
 * ISO date string (transit + partner-day items).
 */
export const RegenerateItemSchema = z.object({
  scope: z.string(),
  partner_id: z.number().nullable(),
  partner_name: z.string().nullable(),
  day: z.string().nullable(),
  state: z.enum(["pending", "running", "done", "failed"]),
  error: z.string().nullable(),
});
export type RegenerateItem = z.infer<typeof RegenerateItemSchema>;

export const RegenerateJobSchema = z.object({
  status: z.enum(["idle", "running", "done"]),
  scopes: z.array(z.string()),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
  items: z.array(RegenerateItemSchema),
});
export type RegenerateJob = z.infer<typeof RegenerateJobSchema>;

/** The four admin-selectable scopes, in the backend's planning order. */
export const REGEN_SCOPES = [
  "natal",
  "synastry",
  "partner_day",
  "transit",
] as const;

/** Keeps the query string clean when no platform filter is active. */
function platformParam(platform: PlatformFilter): string {
  return platform === "all" ? "" : `&platform=${platform}`;
}

export function useUserSearch(q: string, platform: PlatformFilter = "all") {
  return useQuery({
    queryKey: ["admin", "users", "search", q, platform],
    queryFn: ({ signal }) =>
      request({
        path: `/api/admin/users/search?q=${encodeURIComponent(q)}${platformParam(platform)}`,
        schema: UserSearchListSchema,
        signal,
      }),
    enabled: q.length >= 1,
  });
}

export function useUsersTop(
  by: "spent" | "credits" | "readings",
  platform: PlatformFilter = "all",
) {
  return useQuery({
    queryKey: ["admin", "users", "top", by, platform],
    queryFn: ({ signal }) =>
      request({
        path: `/api/admin/users/top?by=${by}&limit=50${platformParam(platform)}`,
        schema: UserTopListSchema,
        signal,
      }),
  });
}

export function useUserCredits(userId: number | null) {
  return useQuery({
    queryKey: ["admin", "users", "credits", userId],
    queryFn: ({ signal }) =>
      request({
        path: `/api/admin/users/${userId}/credits`,
        schema: UserCreditsViewSchema,
        signal,
      }),
    enabled: userId !== null,
  });
}

export function useUserPayments(userId: number | null) {
  return useQuery({
    queryKey: ["admin", "payments", userId],
    queryFn: ({ signal }) =>
      request({
        path: `/api/admin/users/${userId}/payments`,
        schema: PaymentListSchema,
        signal,
      }),
    enabled: userId !== null,
  });
}

export function useGrantCredits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      target_user_id: number;
      amount: number;
      feature: string;
      note?: string;
    }) =>
      request({
        method: "POST",
        path: "/api/admin/credits/grant",
        body,
        schema: UserCreditsViewSchema,
      }),
    onSuccess: (data) =>
      qc.invalidateQueries({ queryKey: ["admin", "users", "credits", data.id] }),
  });
}

export function useSetAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: number; is_admin: boolean }) =>
      request({
        method: "PATCH",
        path: `/api/admin/users/${args.userId}/admin`,
        body: { is_admin: args.is_admin },
        schema: UserAdminSchema,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useRegenerateJob(userId: number | null) {
  return useQuery({
    queryKey: ["admin", "users", "regenerate", userId],
    queryFn: ({ signal }) =>
      request({
        path: `/api/admin/users/${userId}/regenerate`,
        schema: RegenerateJobSchema,
        signal,
      }),
    enabled: userId !== null,
    // Poll every 2 s while the job runs; stop on a terminal status
    // (react-query v5 function form, the `useSubscriptionMe` shape).
    refetchInterval: (query) =>
      query.state.data?.status === "running" ? 2_000 : false,
    refetchIntervalInBackground: false,
  });
}

export function useStartRegenerate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: number; scopes: string[] }) =>
      request({
        method: "POST",
        path: `/api/admin/users/${args.userId}/regenerate`,
        body: { scopes: args.scopes },
        schema: RegenerateJobSchema,
      }),
    onSuccess: (_data, args) =>
      qc.invalidateQueries({
        queryKey: ["admin", "users", "regenerate", args.userId],
      }),
  });
}

export function useRefundPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { payment_id: number; note?: string }) =>
      request({
        method: "POST",
        path: "/api/admin/refund",
        body,
        schema: RefundResponseSchema,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "payments"] });
      qc.invalidateQueries({ queryKey: ["admin", "users", "credits"] });
    },
  });
}
